import { useTranslation } from 'react-i18next';
import { ClipboardList } from 'lucide-react';
import { type StatusItem, getStatusIcon } from './types';

interface StatusItemCardProps {
  item: StatusItem;
  onClick: () => void;
}

const STATUS_TO_KEY: Record<string, string> = {
  FULL: 'full',
  OK: 'ok',
  LOW: 'low',
  EMPTY: 'empty',
  WORKING: 'working',
  ISSUE: 'issue',
  NOT_WORKING: 'notWorking',
  CLEAN: 'clean',
  NEEDS_ATTENTION: 'needsAttention',
  DIRTY: 'dirty',
  COMFORTABLE: 'comfortable',
  UNCOMFORTABLE: 'uncomfortable',
};

function getStatusVariant(status: string): 'ok' | 'warning' | 'bad' | 'unknown' {
  const goodStatuses = ['FULL', 'OK', 'WORKING', 'CLEAN', 'COMFORTABLE'];
  const warningStatuses = ['LOW', 'NEEDS_ATTENTION', 'ISSUE'];
  const badStatuses = ['EMPTY', 'NOT_WORKING', 'DIRTY', 'UNCOMFORTABLE'];

  if (goodStatuses.includes(status)) return 'ok';
  if (warningStatuses.includes(status)) return 'warning';
  if (badStatuses.includes(status)) return 'bad';
  return 'unknown';
}

const cardStyles = {
  ok: 'border-2 border-forest/30 bg-gradient-to-br from-forest-wash via-white to-forest-wash',
  warning: 'border-2 border-gold/30 bg-gradient-to-br from-gold/5 via-white to-gold/10',
  bad: 'border-2 border-critical/30 bg-gradient-to-br from-critical/5 via-white to-critical/10',
  unknown: 'border-2 border-warm-border bg-white',
};

const indicatorStyles = {
  ok: 'bg-forest shadow-[0_0_12px_rgba(45,106,79,0.6)]',
  warning: 'bg-gold shadow-[0_0_12px_rgba(196,159,56,0.6)]',
  bad: 'bg-critical shadow-[0_0_12px_rgba(220,38,38,0.6)] animate-pulse',
  unknown: 'bg-steel',
};

const badgeStyles = {
  ok: 'bg-forest-wash text-forest font-bold',
  warning: 'bg-gold/15 text-gold font-bold',
  bad: 'bg-critical/15 text-critical font-bold',
  unknown: 'bg-muted text-muted-foreground font-bold',
};

export function StatusItemCard({ item, onClick }: StatusItemCardProps) {
  const { t } = useTranslation('scout');
  const variant = getStatusVariant(item.currentStatus);
  const statusIcon = getStatusIcon(item.currentStatus);
  const statusKey = STATUS_TO_KEY[item.currentStatus] || item.currentStatus.toLowerCase();

  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center p-4 rounded-3xl min-h-[120px] w-full shadow-[0_4px_16px_rgba(45,106,79,0.15)] transition-all duration-300 ease-out hover:shadow-[0_12px_32px_rgba(45,106,79,0.2)] hover:-translate-y-2 hover:scale-[1.02] active:scale-[0.95] active:shadow-[0_2px_8px_rgba(45,106,79,0.1)] ${cardStyles[variant]}`}
      aria-label={`${item.statusType.name}: ${t(`status.levels.${statusKey}`)}. ${t('status.tapItemToUpdate')}`}
    >
      {/* Status indicator dot */}
      <span
        className={`absolute top-3 end-3 w-3.5 h-3.5 rounded-full ring-[3px] ring-white ${indicatorStyles[variant]}`}
        aria-hidden="true"
      />

      {/* Icon container */}
      <div className="w-14 h-14 rounded-2xl bg-primary/[0.08] flex items-center justify-center mb-2 shadow-inner">
        {item.statusType.icon ? (
          <span className="text-3xl" aria-hidden="true">
            {item.statusType.icon}
          </span>
        ) : (
          <ClipboardList className="w-7 h-7 text-primary" aria-hidden="true" />
        )}
      </div>

      {/* Name */}
      <span className="text-small font-bold text-center line-clamp-1 text-primary mb-2">
        {item.statusType.name}
      </span>

      {/* Status badge */}
      <div
        className={`px-3 py-1.5 rounded-full text-caption flex items-center gap-1.5 shadow-sm ${badgeStyles[variant]}`}
      >
        <span aria-hidden="true">{statusIcon}</span>
        <span>{t(`status.levels.${statusKey}`)}</span>
      </div>
    </button>
  );
}
